import { Routes } from '@angular/router';
import { AppLayoutComponent } from '@shared/layout/app-layout/app-layout.component';
import { authGuard } from '@shared/auth/guards/auth.guard';
import { redirectAuthenticatedGuard } from '@shared/auth/guards/redirect-authenticated.guard';
import { adminGuard } from '@shared/auth/guards/admin.guard';
import { superadminGuard } from '@shared/auth/guards/superadmin.guard';
import { permissionGuard, requireActionGuard } from '@shared/auth/guards/permission.guard';
import { transferenciaTokenGuard } from '@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-token.guard';
import { transferenciaConfirmarResolver } from '@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.resolver';
import { prosellBuyerGuard, prosellSellerGuard } from '@prosell/feature-catalog/guards/prosell-role.guards';

export const routes: Routes = [
  // La raíz entra al login del panel Prosell. La tienda sigue disponible en `/tienda`, con su
  // propio acceso desde el pie del panel.
  // `redirectTo` con un string literal es una redirección ESTÁTICA — el Router la resuelve al
  // hacer el match de la ruta, sin guard ni petición de red. **No la conviertas en un guard
  // asíncrono**: eso ya se intentó aquí mismo y dejaba la pantalla en blanco, porque dos guards de
  // rutas distintas competían en una sola navegación (ver `git log` de la fase 5 de M04/32).
  {
    path: '',
    redirectTo: 'auth/login',
    pathMatch: 'full',
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'inicio',
        loadComponent: () => import('@partnercore/feature-home/inicio/inicio.component').then(m => m.InicioComponent),
        title: 'titles.inicio',
      },
      {
        path: 'notifications',
        loadComponent: () => import('@partnercore/feature-modules/modulos/control-general/notificaciones/notifications/notifications.component').then(m => m.NotificationsComponent),
        title: 'titles.notificaciones',
      },
      {
        path: 'configuracion',
        loadComponent: () => import('@partnercore/feature-settings/configuracion/configuracion.component').then(m => m.ConfiguracionComponent),
        title: 'titles.configuracion',
      },
      {
        path: 'soporte',
        loadComponent: () => import('@partnercore/feature-settings/soporte/soporte.component').then(m => m.SoporteComponent),
        title: 'titles.soporte',
      },
      {
        path: 'configuracion/transferir',
        loadComponent: () => import('@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.component').then(m => m.TransferenciaConfirmarComponent),
        title: 'titles.confirmTransfer',
        canActivate: [transferenciaTokenGuard],
        resolve: { transferenciaInfo: transferenciaConfirmarResolver },
      },
      // Dashboard Sistema
      {
        path: 'dashboard-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'dashboard-general',
            pathMatch: 'full',
          },
          {
            path: 'dashboard-general',
            loadComponent: () => import('@partnercore/feature-dashboard/dashboard/general/dashboard-general.component').then(m => m.DashboardGeneralComponent),
            canActivate: [permissionGuard],
            title: 'titles.dashboardGeneral',
          },
        ],
      },
      // Módulos Admin Sistema
      {
        path: 'admin-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () => import('@partnercore/feature-admin/users/users.component').then(m => m.UsersComponent),
            canActivate: [permissionGuard],
            title: 'titles.usuariosSistema',
          },
          {
            path: 'permisos',
            loadComponent: () => import('@partnercore/feature-admin/permisos').then(m => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'titles.permisosSistema',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () => import('@partnercore/feature-admin/permisos-editar').then(m => m.PermisosEditarComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.permisosSistema',
          },
          {
            path: 'roles',
            loadComponent: () => import('@partnercore/feature-admin/roles/roles.component').then(m => m.RolesComponent),
            canActivate: [permissionGuard],
            title: 'titles.rolesSistema',
          },
          {
            path: 'perfil',
            loadComponent: () => import('@partnercore/feature-profile/perfil/perfil.component').then(m => m.PerfilComponent),
            canActivate: [permissionGuard],
            title: 'titles.perfilSistema',
          },
          {
            path: 'empresas',
            loadComponent: () => import('@partnercore/feature-admin/empresas/empresas.component').then(m => m.EmpresasComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresasSistema',
          },
          {
            path: 'empresas/:id',
            loadComponent: () => import('@partnercore/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(m => m.EmpresaDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresaDetalle',
          },
          {
            path: 'clientes',
            loadComponent: () => import('@partnercore/feature-admin/clientes/clientes.component').then(m => m.ClientesComponent),
            canActivate: [permissionGuard],
            title: 'titles.clientesSistema',
          },
          {
            path: 'planes',
            loadComponent: () => import('@partnercore/feature-admin/planes/planes.component').then(m => m.PlanesComponent),
            canActivate: [permissionGuard],
            title: 'titles.planesSistema',
          },
          {
            path: 'planes/:id',
            loadComponent: () => import('@partnercore/feature-admin/planes/components/plan-detail/plan-detail.component').then(m => m.PlanDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.planesSistema',
          },
          {
            path: 'productos',
            loadComponent: () => import('@partnercore/feature-admin/productos/productos.component').then(m => m.ProductosComponent),
            canActivate: [permissionGuard],
            title: 'titles.productosSistema',
          },
          // M05.1: tasa de IGV y umbral C2C (configuración de plataforma de la facturación).
          {
            path: 'facturacion-plataforma',
            loadComponent: () =>
              import('@prosell/feature-invoicing/plataforma/facturacion-plataforma.component').then(m => m.FacturacionPlataformaComponent),
            canActivate: [permissionGuard],
            title: 'titles.facturacionSistema',
          },
        ],
      },
      // Seguridad Sistema (SYS) — ruta top-level para que coincida con la URL sembrada en DB: /seguridad-sistema/*
      {
        path: 'seguridad-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () => import('@partnercore/feature-sessions/sesiones/sesiones.component').then(m => m.SesionesComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'titles.sesionesSistema',
          },
          {
            path: 'auditorias',
            loadComponent: () => import('@partnercore/feature-audit/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'titles.auditSistema',
          },
        ],
      },
      // Dashboard Control General
      {
        path: 'dashboard',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full',
          },
          {
            path: 'general',
            loadComponent: () => import('@partnercore/feature-dashboard/dashboard/general/dashboard-general.component').then(m => m.DashboardGeneralComponent),
            canActivate: [permissionGuard],
            title: 'titles.dashboardGeneral',
          },
        ],
      },
      // Módulos Administrativos
      {
        path: 'admin',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () => import('@partnercore/feature-admin/users/users.component').then(m => m.UsersComponent),
            canActivate: [permissionGuard],
            title: 'titles.usuarios',
          },
          {
            path: 'permisos',
            loadComponent: () => import('@partnercore/feature-admin/permisos').then(m => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'titles.permisos',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () => import('@partnercore/feature-admin/permisos-editar').then(m => m.PermisosEditarComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.permisos',
          },
          {
            path: 'roles',
            loadComponent: () => import('@partnercore/feature-admin/roles/roles.component').then(m => m.RolesComponent),
            canActivate: [permissionGuard],
            title: 'titles.rolesAdmin',
          },
          {
            path: 'perfil',
            loadComponent: () => import('@partnercore/feature-profile/perfil/perfil.component').then(m => m.PerfilComponent),
            canActivate: [permissionGuard],
            title: 'titles.perfil',
          },
          {
            path: 'empresas',
            loadComponent: () => import('@partnercore/feature-admin/empresas/empresas.component').then(m => m.EmpresasComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresas',
          },
          {
            path: 'empresas/:id',
            loadComponent: () => import('@partnercore/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(m => m.EmpresaDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresaDetalle',
          },
        ],
      },
      // Estructura
      {
        path: 'estructura-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'modulos-sistema',
            pathMatch: 'full',
          },
          {
            path: 'modulos-sistema',
            loadComponent: () => import('@partnercore/feature-modules/modulos/module.component').then(m => m.ModuleComponent),
            canActivate: [permissionGuard],
            title: 'titles.modulosSistema',
          },
        ],
      },
      // Maestros Sistema
      {
        path: 'maestros-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () => import('@partnercore/feature-currencies/currencies/currencies.component').then(m => m.CurrenciesComponent),
            canActivate: [permissionGuard],
            title: 'titles.monedasSistema',
          },
        ],
      },
      // Maestros
      {
        path: 'maestros',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () => import('@partnercore/feature-currencies/currencies/currencies.component').then(m => m.CurrenciesComponent),
            canActivate: [permissionGuard],
            title: 'titles.monedas',
          },
        ],
      },
      // Marketplace Prosell — taxonomía REAL de Prosell (M01..M10, marketplace multivendedor), no la
      // tabla de CoreShop con la que arrancó el proyecto (ver Bitácora Prosell). Las URLs de pantallas
      // ya construidas siguen bajo /prosell/* (sembradas en DB, no se renombran); los módulos nuevos
      // usan un prefijo propio por dominio y, si no tienen pantalla aún, cargan ComingSoonComponent.
      {
        path: 'prosell',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@prosell/feature-catalog/hub/prosell-hub.component').then(m => m.ProsellHubComponent),
            canActivate: [permissionGuard],
            title: 'titles.prosellHub',
          },
          // M01 Descubrimiento y Catálogo
          {
            path: 'productos-maestro',
            loadComponent: () =>
              import('@prosell/feature-catalog/catalogo/catalogo.component').then(m => m.CatalogoComponent),
            canActivate: [permissionGuard, prosellSellerGuard],
            title: 'titles.productosMaestro',
          },
          {
            path: 'ofertas',
            loadComponent: () =>
              import('@prosell/feature-catalog/ofertas/ofertas.component').then(m => m.OfertasComponent),
            canActivate: [permissionGuard, prosellSellerGuard],
            title: 'titles.ofertas',
          },
          {
            path: 'preguntas-vendedor',
            loadComponent: () =>
              import('@prosell/feature-catalog/preguntas-vendedor/preguntas-vendedor.component').then(m => m.PreguntasVendedorComponent),
            canActivate: [permissionGuard, prosellSellerGuard],
            title: 'titles.preguntasVendedor',
          },
          {
            path: 'comprar',
            loadComponent: () =>
              import('@prosell/feature-catalog/comprar/comprar.component').then(m => m.ComprarComponent),
            canActivate: [permissionGuard, prosellBuyerGuard],
            title: 'titles.comprar',
          },
          {
            path: 'producto/:variantId',
            loadComponent: () =>
              import('@prosell/feature-catalog/producto/producto.component').then(m => m.ProductoComponent),
            canActivate: [permissionGuard, prosellBuyerGuard],
            title: 'titles.prosellProducto',
          },
          {
            path: 'sync-catalogo',
            loadComponent: () =>
              import('@prosell/feature-catalog/catalog-sync/catalog-sync.component').then(m => m.CatalogSyncComponent),
            canActivate: [permissionGuard],
            title: 'titles.syncCatalogo',
          },
          // M02 Checkout y Pagos
          {
            path: 'carrito',
            loadComponent: () =>
              import('@prosell/feature-cart-checkout/descuentos/descuentos.component').then(m => m.DescuentosComponent),
            canActivate: [permissionGuard, prosellSellerGuard],
            title: 'titles.carrito',
          },
          // El checkout ya no vive acá: es del comprador y su única pantalla es `/tienda/checkout`.
          // La fila de módulo `/prosell/checkout` se conserva en el backend (plegada, status=0) para no
          // perder los permisos ya otorgados por rol; simplemente ninguna ruta la usa.
          // M03.3.1 Cotización por Volumen
          {
            path: 'cotizaciones',
            loadComponent: () =>
              import('@prosell/feature-marketplace-extra/cotizaciones/cotizaciones.component').then(m => m.CotizacionesComponent),
            canActivate: [permissionGuard],
            title: 'titles.cotizaciones',
          },
          // M03.1 RF-025: administración de listas de precios y auditoría de cambios.
          {
            path: 'price-books',
            loadComponent: () =>
              import('@prosell/feature-marketplace-extra/price-books/price-books.component').then(m => m.PriceBooksComponent),
            canActivate: [permissionGuard],
            title: 'titles.priceBooks',
          },
          // M06.1 Panel de Disputas — módulo único con vistas según rol.
          {
            path: 'admin/disputas',
            loadComponent: () =>
              import('@prosell/feature-orders/disputas/disputas.component').then(m => m.DisputasComponent),
            canActivate: [permissionGuard],
            title: 'titles.disputas',
          },
          {
            path: 'admin/disputas/mediacion/:disputeId',
            loadComponent: () =>
              import('@prosell/feature-orders/disputas-admin/disputa-expediente.component').then(m => m.DisputaExpedienteComponent),
            canActivate: [permissionGuard],
            title: 'titles.disputas',
          },
          {
            path: 'admin/disputas/:disputeId',
            loadComponent: () =>
              import('@prosell/feature-orders/disputas/disputa-vendedor-detalle.component').then(m => m.DisputaVendedorDetalleComponent),
            canActivate: [permissionGuard],
            title: 'titles.disputas',
          },
          {
            // M02 Checkout y Pagos — ciclo de pedido (soporte)
            path: 'pedidos',
            loadComponent: () =>
              import('@prosell/feature-orders/pedidos/pedidos.component').then(m => m.PedidosComponent),
            canActivate: [permissionGuard, prosellBuyerGuard],
            title: 'titles.pedidos',
          },
          {
            path: 'devoluciones',
            loadComponent: () =>
              import('@prosell/feature-orders/devoluciones/devoluciones.component').then(m => m.DevolucionesComponent),
            canActivate: [permissionGuard],
            title: 'titles.devoluciones',
          },
          // RF-003: plazo de devolución por categoría (consola de la plataforma)
          {
            path: 'admin/plazos-categoria',
            loadComponent: () =>
              import('@prosell/feature-orders/plazos-categoria-admin/plazos-categoria-admin.component').then(m => m.PlazosCategoriaAdminComponent),
            canActivate: [adminGuard],
            title: 'titles.plazosCategoria',
          },
          // M08.8.2 Reportes y Analítica Ejecutiva
          {
            path: 'reportes-ejecutivos',
            loadComponent: () =>
              import('@prosell/feature-reports/reportes-ejecutivos/reportes-ejecutivos.component').then(m => m.ReportesEjecutivosComponent),
            canActivate: [permissionGuard],
            title: 'titles.reportesEjecutivos',
          },
          // M07 Integraciones API / ERP
          {
            path: 'webhooks',
            loadComponent: () =>
              import('@prosell/feature-integrations/webhooks/webhooks.component').then(m => m.WebhooksComponent),
            canActivate: [permissionGuard],
            title: 'titles.webhooks',
          },
          {
            path: 'developer-portal',
            loadComponent: () =>
              import('@prosell/feature-integrations/developer-portal/developer-portal.component').then(m => m.DeveloperPortalComponent),
            canActivate: [permissionGuard],
            title: 'titles.developerPortal',
          },
          // M10.10.1 Panel de Canal y Control de Precio/Territorio (B2B2C)
          {
            path: 'canales',
            loadComponent: () =>
              import('@prosell/feature-integrations/canales/canales.component').then(m => m.CanalesComponent),
            canActivate: [permissionGuard],
            title: 'titles.canales',
          },
          {
            path: 'canales/:channelId/panel',
            loadComponent: () =>
              import('@prosell/feature-integrations/panel-marca/panel-marca.component').then(m => m.PanelMarcaComponent),
            canActivate: [permissionGuard],
            title: 'titles.panelMarca',
          },
          // M04.4.2 Gestión de Línea de Crédito
          {
            path: 'credito',
            loadComponent: () =>
              import('@prosell/feature-marketplace-extra/credito/credito.component').then(m => m.CreditoComponent),
            canActivate: [permissionGuard],
            title: 'titles.credito',
          },
          {
            path: 'cartera-credito',
            loadComponent: () =>
              import('@prosell/feature-marketplace-extra/cartera-credito/cartera-credito.component').then(m => m.CarteraCreditoComponent),
            canActivate: [adminGuard],
            title: 'titles.carteraCredito',
            data: { code: 'M04.4.2' },
          },
          // M09 Comercio entre Particulares (C2C)
          {
            path: 'anuncios-c2c/:id',
            loadComponent: () =>
              import('@prosell/feature-marketplace-extra/anuncio-detalle/anuncio-detalle.component').then(m => m.AnuncioDetalleComponent),
            canActivate: [permissionGuard],
            title: 'titles.anuncioDetalle',
          },
          {
            path: 'anuncios-c2c',
            loadComponent: () =>
              import('@prosell/feature-marketplace-extra/anuncios-c2c/anuncios-c2c.component').then(m => m.AnunciosC2cComponent),
            canActivate: [permissionGuard],
            title: 'titles.anunciosC2c',
          },
          {
            path: 'c2c/chat/:conversationId',
            loadComponent: () =>
              import('@prosell/feature-marketplace-extra/chat-c2c/chat-c2c.component').then(m => m.ChatC2cComponent),
            canActivate: [permissionGuard],
            title: 'titles.chatC2c',
          },
          {
            path: 'c2c/puntos-encuentro',
            loadComponent: () =>
              import('@prosell/feature-marketplace-extra/puntos-encuentro/puntos-encuentro.component').then(m => m.PuntosEncuentroComponent),
            canActivate: [permissionGuard],
            title: 'titles.puntosEncuentro',
          },
          {
            path: 'c2c/admin/puntos-encuentro',
            loadComponent: () =>
              import('@prosell/feature-marketplace-extra/puntos-encuentro-admin/puntos-encuentro-admin.component').then(m => m.PuntosEncuentroAdminComponent),
            canActivate: [adminGuard],
            title: 'titles.puntosEncuentroAdmin',
            data: { code: 'M09.9.2' },
          },
          // M08 Administración de Plataforma — seguridad/permisos (soporte)
          {
            path: 'acceso-emergencia',
            loadComponent: () =>
              import('@prosell/feature-marketplace-extra/emergency-access/emergency-access.component').then(m => m.EmergencyAccessComponent),
            canActivate: [permissionGuard],
            title: 'titles.accesoEmergencia',
          },
          {
            path: 'delegaciones',
            loadComponent: () =>
              import('@prosell/feature-marketplace-extra/delegaciones/delegaciones.component').then(m => m.DelegacionesComponent),
            canActivate: [permissionGuard],
            title: 'titles.delegaciones',
          },
          // Submódulos de la tabla real de Prosell sin pantalla propia todavía — un ítem de sidebar
          // por submódulo (no por pantalla), cargan el placeholder compartido "En construcción".
          {
            path: 'transparencia-costos',
            loadComponent: () =>
              import('@prosell/feature-catalog/transparencia-costos/transparencia-costos.component').then(m => m.TransparenciaCostosComponent),
            canActivate: [permissionGuard],
            title: 'titles.transparenciaCostos',
            data: { code: 'M01.1.3' }
          },
          {
            path: 'historial-reorden',
            loadComponent: () =>
              import('@prosell/feature-cart-checkout/historial-reorden/historial-reorden.component').then(m => m.HistorialReordenComponent),
            canActivate: [permissionGuard],
            title: 'titles.historialReorden',
            data: { code: 'M03.3.2' }
          },
          { path: 'riesgo-crediticio', loadComponent: () => import('@shared/components/common/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent), title: 'titles.riesgoCrediticio', data: { code: 'M04.4.1' } },
          {
            path: 'ranking-config',
            loadComponent: () =>
              import('@prosell/feature-catalog/ranking-config/ranking-config.component').then(m => m.RankingConfigComponent),
            canActivate: [superadminGuard],
            title: 'titles.rankingConfig',
            data: { code: 'M01.1.1' }
          },
          {
            path: 'moderacion-fichas',
            loadComponent: () =>
              import('@prosell/feature-catalog/moderacion-fichas/moderacion-fichas.component').then(m => m.ModeracionFichasComponent),
            canActivate: [adminGuard],
            title: 'titles.moderacionFichas',
            data: { code: 'M01.1.2' }
          },
          {
            path: 'moderacion-resenas',
            loadComponent: () =>
              import('@prosell/feature-catalog/moderacion-resenas/moderacion-resenas.component').then(m => m.ModeracionResenasComponent),
            canActivate: [adminGuard],
            title: 'titles.moderacionResenas',
            data: { code: 'M01.1.2' }
          },
          {
            path: 'deteccion-fraude',
            loadComponent: () =>
              import('@prosell/feature-orders/fraude-admin/fraude-admin.component').then(m => m.FraudeAdminComponent),
            canActivate: [adminGuard],
            title: 'titles.deteccionFraude',
            data: { code: 'M06.6.2' }
          },
          {
            path: 'incidentes-citas',
            loadComponent: () =>
              import('@prosell/feature-marketplace-extra/incidentes-admin/incidentes-admin.component').then(m => m.IncidentesAdminComponent),
            canActivate: [adminGuard],
            title: 'titles.incidentesAdmin',
            data: { code: 'M09.9.2' },
          },
          {
            path: 'moderacion-anuncios',
            loadComponent: () =>
              import('@prosell/feature-marketplace-extra/moderacion-anuncios/moderacion-anuncios.component').then(m => m.ModeracionAnunciosComponent),
            canActivate: [adminGuard],
            title: 'titles.moderacionAnuncios',
            data: { code: 'M09.9.1' },
          },
          {
            path: 'verificacion-vendedores',
            loadComponent: () =>
              import('@prosell/feature-orders/verificacion-vendedor-admin/verificacion-vendedor-admin.component').then(m => m.VerificacionVendedorAdminComponent),
            canActivate: [adminGuard],
            title: 'titles.verificacionVendedores',
            data: { code: 'M06.6.3' },
          },
          {
            path: 'kyc-revendedor',
            loadComponent: () =>
              import('@prosell/feature-orders/kyc-reseller/kyc-reseller.component').then(m => m.KycResellerComponent),
            canActivate: [permissionGuard],
            title: 'titles.kycReseller',
            data: { code: 'M06.6.3' },
          },
          { path: 'c2c/chat', loadComponent: () => import('@shared/components/common/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent), title: 'titles.chatSeguroC2c', data: { code: 'M09.9.3' } },
          { path: 'tienda-virtual', loadComponent: () => import('@shared/components/common/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent), title: 'titles.tiendaVirtualRevendedor', data: { code: 'M10.10.2' } },
          { path: 'reparto-comision', loadComponent: () => import('@shared/components/common/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent), title: 'titles.repartoComision', data: { code: 'M10.10.3' } },
        ],
      },
      // M01 Descubrimiento y Catálogo — Storefront (soporte, sin código propio en la tabla real)
      {
        path: 'storefront',
        canActivate: [permissionGuard],
        children: [
          { path: 'temas', loadComponent: () => import('@prosell/feature-storefront-design/temas/storefront-temas.component').then(m => m.StorefrontTemasComponent), title: 'titles.storefrontTemas', data: { code: 'M01 · soporte' } },
          { path: 'editor-visual', loadComponent: () => import('@prosell/feature-storefront-design/banners/storefront-banners.component').then(m => m.StorefrontBannersComponent), title: 'titles.storefrontEditorVisual', data: { code: 'M01 · soporte' } },
          { path: 'paginas-blog', loadComponent: () => import('@prosell/feature-storefront-design/menus/storefront-menus.component').then(m => m.StorefrontMenusComponent), title: 'titles.storefrontPaginasBlog', data: { code: 'M01 · soporte' } },
          { path: 'seo', loadComponent: () => import('@prosell/feature-storefront-design/seo/storefront-seo.component').then(m => m.StorefrontSeoComponent), title: 'titles.storefrontSeo', data: { code: 'M01 · soporte' } },
        ],
      },
      // M01.1.2 Ficha de Producto / Artículo (placeholders restantes)
      {
        path: 'catalogo',
        canActivate: [permissionGuard],
        children: [
          { path: 'colecciones', loadComponent: () => import('@prosell/feature-catalog/colecciones/colecciones.component').then(m => m.ColeccionesComponent), title: 'titles.catalogoColecciones', data: { code: 'M01.1.2' } },
          { path: 'variantes', loadComponent: () => import('@shared/components/common/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent), title: 'titles.catalogoVariantes', data: { code: 'M01.1.2' } },
        ],
      },
      // M01 Descubrimiento y Catálogo — Inventario (soporte, sin código propio en la tabla real)
      {
        path: 'inventario',
        canActivate: [permissionGuard],
        children: [
          { path: 'stock', loadComponent: () => import('@prosell/feature-catalog/inventario/inventario.component').then(m => m.InventarioComponent), title: 'titles.inventarioStock', data: { code: 'M01 · soporte' } },
          { path: 'alertas', loadComponent: () => import('@prosell/feature-catalog/inventario/inventario.component').then(m => m.InventarioComponent), title: 'titles.inventarioAlertas', data: { code: 'M01 · soporte' } },
          { path: 'ubicaciones', loadComponent: () => import('@prosell/feature-catalog/inventario/inventario.component').then(m => m.InventarioComponent), title: 'titles.inventarioUbicaciones', data: { code: 'M01 · soporte' } },
        ],
      },
      // M02.2.2 Pagos y Split Payment / M02.2.3 Transparencia y Reembolsos (placeholders)
      {
        path: 'pagos',
        canActivate: [permissionGuard],
        children: [
          // M02/02 Fase 1 (RF-011): "mis transacciones" del vendedor — reemplaza el placeholder ComingSoon.
          { path: 'procesamiento', loadComponent: () => import('@prosell/feature-orders/pagos-vendedor/pagos-vendedor.component').then(m => m.PagosVendedorComponent), title: 'titles.pagosProcesamiento', data: { code: 'M02.2.2' } },
          // Fase 4 RF-012: catálogo de métodos de pago, solo lectura (reemplaza el placeholder ComingSoon).
          { path: 'metodos-pe', loadComponent: () => import('@prosell/feature-orders/payment-methods-admin/payment-methods-admin.component').then(m => m.PaymentMethodsAdminComponent), title: 'titles.pagosMetodosPe', data: { code: 'M02.2.2' } },
          // Pasarela F7: consola de cuentas de pago (estado TEST/LIVE, webhook, probar conexión).
          { path: 'cuentas-pasarela', loadComponent: () => import('@prosell/feature-orders/cuentas-pasarela-admin/cuentas-pasarela-admin.component').then(m => m.CuentasPasarelaAdminComponent), title: 'titles.pagosCuentasPasarela', data: { code: 'M02.2.2' } },
          { path: 'reembolsos', loadComponent: () => import('@prosell/feature-orders/reembolsos-admin/reembolsos-admin.component').then(m => m.ReembolsosAdminComponent), title: 'titles.pagosReembolsos', data: { code: 'M02.2.3' } },
        ],
      },
      // M02 Checkout y Pagos — Envíos. Las reglas de envío y el despacho viven ahora en modales de /prosell/carrito.
      {
        path: 'envios',
        canActivate: [permissionGuard],
        children: [
          { path: 'couriers', loadComponent: () => import('@shared/components/common/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent), title: 'titles.enviosCouriers', data: { code: 'M02 · soporte' } },
          { path: 'tracking', loadComponent: () => import('@shared/components/common/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent), title: 'titles.enviosTracking', data: { code: 'M02 · soporte' } },
        ],
      },
      // M05.5.1 Emisión de Comprobantes Multivendedor (notas de crédito y débito: M05.1 F4)
      {
        path: 'facturacion',
        canActivate: [permissionGuard],
        children: [
          {
            path: 'comprobantes',
            loadComponent: () =>
              import('@prosell/feature-invoicing/comprobantes/comprobantes.component').then(m => m.ComprobantesComponent),
            title: 'titles.facturacionComprobantes',
            data: { code: 'M05.5.1' }
          },
          { path: 'notas', redirectTo: 'comprobantes', pathMatch: 'full' },
          {
            path: 'conciliacion',
            loadComponent: () =>
              import('@prosell/feature-orders/conciliacion-admin/conciliacion-admin.component').then(m => m.ConciliacionAdminComponent),
            canActivate: [adminGuard],
            title: 'titles.facturacionConciliacion',
            data: { code: 'M05.5.2' }
          },
        ],
      },
      // M08.8.2 Reportes y Analítica Ejecutiva (placeholders restantes)
      {
        path: 'informes',
        canActivate: [permissionGuard],
        children: [
          { path: 'analitica', loadComponent: () => import('@shared/components/common/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent), title: 'titles.informesAnalitica', data: { code: 'M08.8.2' } },
          { path: 'roas', loadComponent: () => import('@shared/components/common/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent), title: 'titles.informesRoas', data: { code: 'M08.8.2' } },
        ],
      },
      // M08 Administración de Plataforma — CRM genérico (soporte, sin código propio en la tabla real)
      {
        path: 'clientes-marketing',
        canActivate: [permissionGuard],
        children: [
          { path: 'cuentas', loadComponent: () => import('@shared/components/common/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent), title: 'titles.clientesMarketingCuentas', data: { code: 'M08 · soporte' } },
          { path: 'segmentos', loadComponent: () => import('@shared/components/common/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent), title: 'titles.clientesMarketingSegmentos', data: { code: 'M08 · soporte' } },
          { path: 'automatizacion', loadComponent: () => import('@shared/components/common/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent), title: 'titles.clientesMarketingAutomatizacion', data: { code: 'M08 · soporte' } },
        ],
      },
      // M07 Integraciones API / ERP (placeholder restante)
      {
        path: 'integraciones',
        canActivate: [permissionGuard],
        children: [
          { path: 'apps', loadComponent: () => import('@shared/components/common/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent), title: 'titles.integracionesApps', data: { code: 'M07 · soporte' } },
        ],
      },
      // M08 Administración de Plataforma — notificaciones (soporte, sin código propio en la tabla real)
      {
        path: 'configuracion-marketplace',
        canActivate: [permissionGuard],
        children: [
          { path: 'notificaciones', loadComponent: () => import('@shared/components/common/coming-soon/coming-soon.component').then(m => m.ComingSoonComponent), title: 'titles.configMarketplaceNotificaciones', data: { code: 'M08 · soporte' } },
        ],
      },
      // Seguridad Control General — ruta top-level para que coincida con la URL sembrada en DB: /seguridad/*
      {
        path: 'seguridad',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () => import('@partnercore/feature-sessions/sesiones/sesiones.component').then(m => m.SesionesComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'titles.sesiones',
          },
          {
            path: 'auditorias',
            loadComponent: () => import('@partnercore/feature-audit/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'titles.audit',
          },
        ],
      },
    ],
  },
  {
    path: 'canal/:slug',
    loadComponent: () =>
      import('@prosell/feature-marketplace-extra/tienda-canal/tienda-canal.component').then(m => m.TiendaCanalComponent),
    title: 'titles.canalSlug',
  },
  // Tienda pública del marketplace Prosell — sin authGuard (ver storefront.routes).
  {
    path: 'tienda',
    loadChildren: () => import('@prosell/feature-storefront/storefront.routes').then(m => m.STOREFRONT_ROUTES),
  },
  // App móvil Prosell (M11) — PWA, Bearer en sessionStorage.
  {
    path: 'm',
    loadChildren: () => import('@prosell/feature-mobile/mobile.routes').then(m => m.MOBILE_ROUTES),
  },
  // Auth
  {
    path: 'auth',
    loadComponent: () => import('@partnercore/feature-auth-pages/auth/auth.component').then(m => m.AuthComponent),
    canActivate: [redirectAuthenticatedGuard],
    children: [
      {
        path: 'login',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/login/login.component-page').then(m => m.LoginPageComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/forgot-password/forgot-password-page.component').then(m => m.ForgotPasswordPageComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/reset-password/reset-password-page.component').then(m => m.ResetPasswordPageComponent),
      },
    ],
  },
  // Error
  {
    path: '404',
    loadComponent: () => import('@partnercore/feature-error-pages/other-page/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'titles.notFound',
  },
  {
    path: '**',
    loadComponent: () => import('@partnercore/feature-error-pages/other-page/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'titles.notFound',
  },
];
